import { create } from 'zustand';
import { teamService } from '../services/team.service';
export const useTeamStore = create((set, get) => ({
    teams: [],
    currentTeam: null,
    loading: false,
    error: null,
    loadTeams: async (organizationId) => {
        set({ loading: true, error: null });
        try {
            const teams = await teamService.fetchTeams(organizationId);
            set({
                teams,
                loading: false,
                // Set current team to first team if not already set
                currentTeam: get().currentTeam || (teams.length > 0 ? teams[0] : null)
            });
        }
        catch (error) {
            console.error('Failed to load teams:', error);
            set({ error: 'Failed to load teams', loading: false });
        }
    },
    setTeams: (teams) => set({ teams }),
    setCurrentTeam: (team) => set({ currentTeam: team }),
    createTeam: async (teamData) => {
        set({ loading: true, error: null });
        try {
            const newTeam = await teamService.createTeam(teamData);
            if (newTeam) {
                set(state => ({
                    teams: [...state.teams, newTeam],
                    loading: false
                }));
            }
            else {
                throw new Error('Failed to create team');
            }
        }
        catch (error) {
            console.error('Failed to create team:', error);
            set({ error: 'Failed to create team', loading: false });
        }
    },
    updateTeam: async (teamId, updateData) => {
        set({ loading: true, error: null });
        try {
            const success = await teamService.updateTeam(teamId, updateData);
            if (success) {
                set(state => ({
                    teams: state.teams.map(team => team.id === teamId
                        ? { ...team, ...updateData }
                        : team),
                    loading: false
                }));
            }
            else {
                throw new Error('Failed to update team');
            }
        }
        catch (error) {
            console.error('Failed to update team:', error);
            set({ error: 'Failed to update team', loading: false });
        }
    },
    deleteTeam: async (teamId) => {
        set({ loading: true, error: null });
        try {
            const success = await teamService.deleteTeam(teamId);
            if (success) {
                set(state => ({
                    teams: state.teams.filter(team => team.id !== teamId),
                    currentTeam: state.currentTeam?.id === teamId ? null : state.currentTeam,
                    loading: false
                }));
            }
            else {
                throw new Error('Failed to delete team');
            }
        }
        catch (error) {
            console.error('Failed to delete team:', error);
            set({ error: 'Failed to delete team', loading: false });
        }
    },
    inviteMember: async (teamId, inviteData) => {
        set({ loading: true, error: null });
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            const newMember = {
                id: `member-${Date.now()}`,
                name: inviteData.email.split('@')[0],
                email: inviteData.email,
                role: inviteData.role,
                joinedAt: new Date().toISOString(),
                isActive: true
            };
            set(state => ({
                teams: state.teams.map(team => team.id === teamId
                    ? {
                        ...team,
                        members: [...team.members, newMember],
                        memberCount: team.memberCount + 1,
                        updatedAt: new Date().toISOString()
                    }
                    : team),
                loading: false
            }));
        }
        catch (error) {
            set({ error: 'Failed to invite member', loading: false });
        }
    },
    removeMember: async (teamId, memberId) => {
        set({ loading: true, error: null });
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            set(state => ({
                teams: state.teams.map(team => team.id === teamId
                    ? {
                        ...team,
                        members: team.members.filter(member => member.id !== memberId),
                        memberCount: team.memberCount - 1,
                        updatedAt: new Date().toISOString()
                    }
                    : team),
                loading: false
            }));
        }
        catch (error) {
            set({ error: 'Failed to remove member', loading: false });
        }
    },
    updateMemberRole: async (teamId, memberId, role) => {
        set({ loading: true, error: null });
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            set(state => ({
                teams: state.teams.map(team => team.id === teamId
                    ? {
                        ...team,
                        members: team.members.map(member => member.id === memberId ? { ...member, role } : member),
                        updatedAt: new Date().toISOString()
                    }
                    : team),
                loading: false
            }));
        }
        catch (error) {
            set({ error: 'Failed to update member role', loading: false });
        }
    },
    getUserPermissions: (teamId, userId) => {
        const team = get().teams.find(t => t.id === teamId);
        if (!team)
            return [];
        const member = team.members.find(m => m.id === userId);
        if (!member)
            return [];
        // Role-based permissions
        const permissions = {
            owner: ['manage_team', 'invite_members', 'remove_members', 'edit_goals', 'view_goals', 'delete_team'],
            admin: ['invite_members', 'remove_members', 'edit_goals', 'view_goals'],
            member: ['view_goals', 'edit_own_goals'],
            guest: ['view_goals']
        };
        return permissions[member.role] || [];
    },
    canUserPerformAction: (teamId, userId, action) => {
        const permissions = get().getUserPermissions(teamId, userId);
        return permissions.includes(action);
    }
}));
